import React, {
  useState, useRef, useCallback, useMemo, useEffect,
} from 'react';
import pluralize from 'pluralize';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import PropTypes from 'prop-types';
import {
  ArrowPrev,
  Download,
  Info,
  Forum,
  Close,
  Statistics,
} from '@picsio/ui/dist/icons';
import {
  IconColorful, Checkbox, Icon, IconButton,
} from '@picsio/ui';
import {
  GoogleDrive, OpenIn, Transcript, ArrowRightSmall,
} from '@picsio/icons';
import { isEmpty } from 'lodash';

import picsioConfig from '../../../../../config';
import useTeammatesNavigate from '../../helpers/components/hooks/useTeammatesNavigate';
import { INTEGRATION_META } from '../../store/helpers/assets/checkAndSetAssetAvailableIntegrations';
import Logger from '../../services/Logger';
import isValidAssetName from '../../helpers/assets/isValidAssetName';
import * as actions from '../../store/actions/assets';
import { Input } from '../../UIComponents';
import localization from '../../shared/strings';
import ua from '../../ua';
import Tooltip from '../Tooltip';

import Button from './Button';
import Group from './Group';
import Logo from './Logo';
import styles from './ToolbarPreviewTop.module.scss';

export default function ToolbarPreviewTop(props) {
  const {
    assetName,
    pages,
    activePageNumber,
    analytics,
    download,
    historyPanel,
    details,
    onClose,
    assetId,
    permissions,
    storageId,
    storageType,
    source = null,
    selectedAssetsIds = [],
    rootCollectionId = '',
    renameInProgress = false,
    isAssetSelected = false,
    selectAsset = () => {},
    isExternalAsset = false,
    assetsActions = {},
    collectionsActions = {},
    transcriptions,
    isSelectable,
    assetAvailableIntegrations = [],
    assetMimeType = '',
    isMultiPage = false,
  } = props;
  const userId = useSelector((state) => state.user._id);

  const { onTeammateNavigateClick } = useTeammatesNavigate('tab=integrations');

  const isMainApp = useMemo(() => picsioConfig.isMainApp, []);
  const [ext, setAssetExt] = useState('');
  const [newName, setAssetNewName] = useState(assetName);
  const inputRef = useRef();
  const dispatch = useDispatch();

  useEffect(() => {
    setAssetNewName(assetName);
  }, [assetName]);

  const initAssetRenaming = () => {
    const assetNameFull = assetName.split('.');
    const assetExt = assetNameFull.pop();
    const assetNewName = assetNameFull.join('.');
    setAssetExt(assetExt);
    setAssetNewName(assetNewName);
  };

  useEffect(() => {
    if (transcriptions?.transcribingParams?.initiatorId === userId
      && transcriptions?.isActive
      && typeof transcriptions?.transcriptViewedByInitiator !== 'undefined'
      && !transcriptions?.transcriptViewedByInitiator
    ) {
      dispatch(actions.setTranscriptViewedByInitiator({ assetId, value: true }));
    }
  }, [userId, assetId, dispatch, transcriptions]);

  const handleEsc = useCallback((event) => {
    const asset = assetName.split('.');
    asset.pop();
    const assetWithoutExt = asset.join('.');
    setAssetNewName(assetWithoutExt);
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, [assetName, setAssetNewName]);

  const handleInputRenameBlur = () => {
    const name = `${newName}.${ext}`;
    if (assetName !== name && newName !== '' && isValidAssetName(name, { showToast: true })) {
      dispatch(actions.rename(assetId, name));
      setAssetNewName(name);
      return;
    }
    handleEsc();
  };

  const handleInputRenameKeyDown = (event) => {
    if (event.keyCode === 13) {
      inputRef.current && inputRef.current.blur();
    }

    if (event.keyCode === 27) {
      handleEsc(event);
    }
  };

  const handleInputRenameChange = () => {
    const assetNewName = inputRef.current.value;
    setAssetNewName(assetNewName);
  };

  const openInLink = useMemo(() => {
    if (isMainApp && (source || storageType === 'gd')) {
      return source || `https://docs.google.com/file/d/${storageId}/edit`;
    }
    return null;
  }, [storageType, storageId, source, isMainApp]);

  const handleClickOpenIn = useCallback(() => {
    Logger.log('User', 'PreviewOpenInNewTab', openInLink);
  }, [openInLink]);

  const handleClickTranscript = () => {
    transcriptions.handler();
  };

  const handleCheckboxSelect = (event) => {
    if (isAssetSelected) {
      Logger.log('User', 'ClipboardPreviewCheckboxUnselect', { assetId });
    } else {
      Logger.log('User', 'ClipboardPreviewCheckboxSelect', { assetId });
    }
    selectAsset({
      id: assetId, value: !isAssetSelected, isRange: event.shiftKey, isOnlyValue: event.altKey,
    });
  };

  const showAllSelectedAssets = useCallback(() => {
    Logger.log('User', 'ClipboardPreviewCounterClick', { selectedAssetsCount: selectedAssetsIds.length });
    assetsActions.getSelectedAssets(selectedAssetsIds);
    collectionsActions.setActiveCollection(rootCollectionId);
  }, [assetsActions, collectionsActions, rootCollectionId, selectedAssetsIds]);

  const onIntegrationsClick = useCallback(() => {
    Logger.log('User', 'PreviewMoreIntegrationsBtnClicked');

    onTeammateNavigateClick();
  }, [onTeammateNavigateClick]);

  const assetsIntegrations = useMemo(() => assetAvailableIntegrations.map((assetsAvailableIntegration) => {
    const {
      href, tooltipKey, IntegrationIcon, testId,
    } = INTEGRATION_META[assetsAvailableIntegration];

    const onIntegrationsIconClick = () => {
      const isAdobe = tooltipKey.includes('ADOBE');

      if (!isAdobe) return;

      Logger.log('User', 'PreviewBrowseInPlugin', { file_format: assetMimeType, plugin_name: tooltipKey });
    };

    return (
      <Tooltip key={assetsAvailableIntegration} content={localization.ASSET_AVAILABILITY_INTEGRATIONS.TOOLTIPS[tooltipKey]} placement="top">
        <a href={href} rel="noreferrer" target="_blank" data-testid={testId} onClick={onIntegrationsIconClick}>
          <IconColorful size="xl">
            <IntegrationIcon />
          </IconColorful>
        </a>
      </Tooltip>
    );
  }), [assetMimeType, assetAvailableIntegrations]);

  const availableIntegrations = useMemo(() => {
    const baseIntegrations = openInLink ? [
      <a
        key="open-in-link"
        className={cn('button-openIn', { toolbarButton: isExternalAsset })}
        href={openInLink}
        data-testid={isExternalAsset ? 'open-in-integration-icon' : 'google-drive-integration-icon'}
        rel="noreferrer"
        target="_blank"
        onClick={handleClickOpenIn}
      >
        <If condition={isExternalAsset}>
          <Icon size="xl">
            <OpenIn />
          </Icon>
        </If>
        <If condition={!isExternalAsset}>
          <Tooltip
            key={localization.ASSET_AVAILABILITY_INTEGRATIONS.TOOLTIPS.GOOGLE_DRIVE}
            content={localization.ASSET_AVAILABILITY_INTEGRATIONS.TOOLTIPS.GOOGLE_DRIVE}
            placement="top"
          >
            <IconColorful size="xl">
              <GoogleDrive />
            </IconColorful>
          </Tooltip>
        </If>
      </a>,
      ...assetsIntegrations] : assetsIntegrations;

    if (baseIntegrations.length >= 1) {
      return [
        ...baseIntegrations.slice(0, 3),
        <Tooltip
          key={localization.ASSET_AVAILABILITY_INTEGRATIONS.TOOLTIPS.MORE_INTEGRATIONS}
          content={localization.ASSET_AVAILABILITY_INTEGRATIONS.TOOLTIPS.MORE_INTEGRATIONS}
          placement="top"
        >
          <IconButton
            size="xl"
            className={styles.integrationsArrow}
            componentProps={{
              'data-testid': 'more-integrations-icon',
            }}
            onClick={onIntegrationsClick}
          >
            <ArrowRightSmall fill="#CCCCCC" />
          </IconButton>
        </Tooltip>,
      ];
    }

    return baseIntegrations;
  }, [assetsIntegrations, handleClickOpenIn, isExternalAsset, onIntegrationsClick, openInLink]);

  return (
    <div className="toolbar toolbarPreviewTop">
      <Group>
        <If condition={onClose}>
          <Choose>
            <When condition={ua.isMobileApp() || ua.browser.isNotDesktop()}>
              <Button
                id="button-backToCatalog"
                icon={() => <ArrowPrev />}
                iconSize="xl"
                onClick={onClose}
              />
            </When>
            <When condition={isMainApp}>
              <Logo handleLogoClick={onClose} />
            </When>
            <Otherwise>
              <Button
                id="button-backToCatalog"
                icon={() => <ArrowPrev />}
                iconSize="xl"
                additionalClass="color"
                onClick={onClose}
              />
            </Otherwise>
          </Choose>
        </If>
      </Group>

      <If condition={!ua.isMobileApp() && !ua.browser.isNotDesktop() && isMainApp && isSelectable}>
        <Group additionalClass="selectedAssetsWrapper">
          <div className="selectedAssets">
            <Checkbox
              checked={isAssetSelected}
              onChange={handleCheckboxSelect}
            />
            <If condition={selectedAssetsIds.length > 0}>
              <div className="selectedAssetsText" data-testid="previewToolbarAssetsCount" onClick={showAllSelectedAssets}>
                {`${pluralize('asset', selectedAssetsIds.length, true)} selected`}
              </div>
            </If>
          </div>
        </Group>
      </If>
      <Group additionalClass="assetNameWrapper previewAssetNameWrapper">
        <If condition={(pages || activePageNumber) && isMultiPage}>
          <span className="multipagePaging">
            <b>{activePageNumber}</b> / {pages}
          </span>
        </If>
        <If condition={isMainApp || picsioConfig.access.fileNameShow}>
          <div className={cn('assetName', { isExternalAsset })}>
            <Input
              type="text"
              error={!isValidAssetName(newName)}
              className="assetRenaming"
              value={newName}
              onChange={handleInputRenameChange}
              onKeyDown={handleInputRenameKeyDown}
              onBlur={handleInputRenameBlur}
              disabled={!isMainApp || (isMainApp && !permissions.editAssetFilename) || renameInProgress}
              customRef={inputRef}
              size={assetName.length}
              onFocus={initAssetRenaming}
            />
          </div>
        </If>
        <If condition={!isEmpty(availableIntegrations) && isMainApp && !picsioConfig.isPim}>
          <div className={cn(styles.integrationsIcons, { [styles.integrationsIconsGap]: !isExternalAsset })}>
            {availableIntegrations}
          </div>
        </If>
        <If condition={(pages || activePageNumber) && !isMultiPage}>
          <span className="multipagePaging">
            <b>{activePageNumber}</b> / {pages}
          </span>
        </If>
      </Group>
      <Group>
        <If condition={download}>
          <Button
            id="button-previewDownloadTop"
            icon={() => <Download />}
            onClick={download.handler}
            additionalClass={download.additionalClass}
          />
        </If>
        <If condition={isMainApp && transcriptions?.showButton && !picsioConfig.isPim}>
          <Button
            id="button-previewTranscripts"
            icon={() => <Transcript />}
            onClick={handleClickTranscript}
            isActive={transcriptions?.isActive}
            tooltip={localization.TOOLBARS.titleTranscriptions}
            tooltipPosition="bottom"
            additionalClass={(
              transcriptions?.transcribingParams?.initiatorId === userId
              && typeof transcriptions?.transcriptViewedByInitiator !== 'undefined'
              && !transcriptions?.transcriptViewedByInitiator)
              ? 'viewed' : ''}
          />
        </If>
        <If condition={isMainApp && !picsioConfig.isPim}>
          <Button
            id="button-previewAnalytics"
            icon={() => <Statistics />}
            onClick={analytics.handler}
            isActive={analytics.isActive}
            tooltip={localization.TOOLBARS.titleAnalytics}
            tooltipPosition="bottom"
          />
        </If>

        <If condition={historyPanel}>
          <Button
            id="button-previewActivity"
            icon={() => <Forum />}
            onClick={historyPanel.handler}
            isActive={historyPanel.isActive}
            tooltip={localization.TOOLBARS.titleActivity}
            tooltipPosition="bottom"
          />
        </If>
        <If condition={details}>
          <Button
            id="button-previewDetails"
            icon={() => <Info />}
            onClick={details.handler}
            isActive={details.isActive}
            tooltip={localization.TOOLBARS.titleDetails}
            tooltipPosition="bottom"
          />
        </If>
        <If condition={onClose}>
          <Button
            id="button-previewClose"
            icon={() => <Close />}
            iconSize="xxl"
            additionalClass="mobileHidden"
            onClick={onClose}
            tooltip={localization.TOOLBARS.titleClosePreview}
            tooltipPosition="bottom"
          />
        </If>
      </Group>
    </div>
  );
}

ToolbarPreviewTop.propTypes = {
  assetName: PropTypes.string,
  pages: PropTypes.string,
  activePageNumber: PropTypes.number,
  analytics: PropTypes.object,
  download: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.object,
  ]),
  historyPanel: PropTypes.object,
  details: PropTypes.object,
  onClose: PropTypes.func,
  historyItems: PropTypes.array,
  isSupportedForDiff: PropTypes.bool,
  setActive: PropTypes.func,
  isDownloadable: PropTypes.bool,
  activeRevisionID: PropTypes.func,
  lastRevisionNumber: PropTypes.number,
  addRevision: PropTypes.func,
  subscriptionFeatures: PropTypes.object,
  isAllowedUploadingRevision: PropTypes.bool,
  isRevisionUploading: PropTypes.bool,
  showRevisions: PropTypes.bool,
  assetId: PropTypes.string,
  permissions: PropTypes.object,
  storageId: PropTypes.string,
  storageType: PropTypes.string,
  source: PropTypes.string,
  transcriptions: PropTypes.object,
  selectedAssetsIds: PropTypes.array,
  isAssetSelected: PropTypes.bool,
  renameInProgress: PropTypes.bool,
  selectAsset: PropTypes.func,
  isExternalAsset: PropTypes.bool,
  assetsActions: PropTypes.shape({
    getSelectedAssets: PropTypes.func,
  }),
  collectionsActions: PropTypes.shape({
    setActiveCollection: PropTypes.func,
  }),
  rootCollectionId: PropTypes.string,
  isSelectable: PropTypes.bool,
  assetAvailableIntegrations: PropTypes.array,
  assetMimeType: PropTypes.string,
  isMultiPage: PropTypes.bool,
};
